import React, { useEffect, useId, useRef, useState } from 'react';
import { FiSearch, FiPlus, FiX } from 'react-icons/fi';
import { billingClientService } from '../../../services/billingClientService';
import { getApiError } from '../../../services/cmsService';
import { useDebounce } from '../../../hooks';
import './ClientSearchAutocomplete.css';

const MIN_CHARS = 2;

/**
 * Type-ahead over active billing clients.
 *
 * With `selected` set it shows the chosen client with a clear button;
 * otherwise a combobox. `onCreateNew(query)` adds a "+ New client" row.
 */
const ClientSearchAutocomplete = ({
  selected = null,
  onSelect,
  onClear,
  onCreateNew,
  placeholder = 'Search clients by name, GSTIN or client ID…',
  autoFocus = false,
  compact = false,
  label,
}) => {
  const baseId = useId();
  const listId = `${baseId}-list`;
  const inputRef = useRef(null);
  const rootRef = useRef(null);
  const requestRef = useRef(0);

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const debounced = useDebounce(query.trim(), 300);

  useEffect(() => {
    if (debounced.length < MIN_CHARS) {
      requestRef.current += 1; // drop any response still in flight
      setResults([]);
      setLoading(false);
      setError('');
      return;
    }

    const requestId = ++requestRef.current;
    setLoading(true);
    setError('');
    billingClientService
      .search(debounced)
      .then((rows) => {
        if (requestId !== requestRef.current) return;
        setResults(rows);
        setActiveIndex(rows.length ? 0 : -1);
      })
      .catch((err) => {
        if (requestId !== requestRef.current) return;
        setResults([]);
        setError(getApiError(err, 'Search failed'));
      })
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false);
      });
  }, [debounced]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const handle = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const options = [
    ...results.map((client) => ({ type: 'client', client })),
    ...(onCreateNew ? [{ type: 'create' }] : []),
  ];

  const choose = (option) => {
    if (!option) return;
    setOpen(false);
    if (option.type === 'create') {
      onCreateNew(query.trim());
      return;
    }
    setQuery('');
    setResults([]);
    onSelect(option.client);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (options.length ? (i + 1) % options.length : -1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (options.length ? (i - 1 + options.length) % options.length : -1));
    } else if (e.key === 'Enter') {
      if (open && activeIndex >= 0 && options[activeIndex]) {
        e.preventDefault();
        choose(options[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.stopPropagation();
        setOpen(false);
      }
    }
  };

  if (selected) {
    return (
      <div className={`csa ${compact ? 'csa-compact' : ''}`}>
        {label && <span className="cf-label">{label}</span>}
        <div className="csa-selected">
          <span className="csa-selected-text">
            <span className="csa-name">{selected.name}</span>
            {!compact && (selected.gstin || selected.clientCode) && (
              <span className="csa-meta">
                {[selected.gstin, selected.clientCode].filter(Boolean).join(' · ')}
              </span>
            )}
          </span>
          {onClear && (
            <button type="button" className="csa-clear" onClick={onClear} title="Clear" aria-label="Clear selected client">
              <FiX />
            </button>
          )}
        </div>
      </div>
    );
  }

  const showList = open && (query.trim().length >= MIN_CHARS || onCreateNew);
  const activeId = activeIndex >= 0 ? `${baseId}-opt-${activeIndex}` : undefined;

  return (
    <div className={`csa ${compact ? 'csa-compact' : ''}`} ref={rootRef}>
      {label && <label className="cf-label" htmlFor={`${baseId}-input`}>{label}</label>}
      <div className="csa-input-wrap">
        <FiSearch className="csa-icon" aria-hidden="true" />
        <input
          ref={inputRef}
          id={`${baseId}-input`}
          type="text"
          className="cf-input csa-input"
          role="combobox"
          aria-expanded={Boolean(showList)}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList ? activeId : undefined}
          autoComplete="off"
          autoFocus={autoFocus}
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {loading && <span className="csa-spinner" aria-hidden="true" />}
      </div>

      {showList && (
        <ul className="csa-list" role="listbox" id={listId}>
          {query.trim().length < MIN_CHARS && (
            <li className="csa-note">Type at least {MIN_CHARS} characters to search.</li>
          )}
          {error && <li className="csa-note error">{error}</li>}
          {!loading && !error && debounced.length >= MIN_CHARS && results.length === 0 && (
            <li className="csa-note">No clients match “{debounced}”.</li>
          )}
          {options.map((option, index) => (
            <li
              key={option.type === 'create' ? 'create' : option.client.id}
              id={`${baseId}-opt-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              className={`csa-option ${index === activeIndex ? 'is-active' : ''} ${option.type === 'create' ? 'csa-create' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => choose(option)}
            >
              {option.type === 'create' ? (
                <span className="csa-create-label"><FiPlus /> New client{query.trim() ? ` “${query.trim()}”` : ''}</span>
              ) : (
                <>
                  <span className="csa-option-main">
                    <span className="csa-name">{option.client.name}</span>
                    <span className="csa-meta">
                      {[option.client.gstin || 'No GSTIN', option.client.clientCode, option.client.stateName]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  {option.client.pendingProjectsCount > 0 && (
                    <span className="csa-pending">
                      {option.client.pendingProjectsCount} pending
                    </span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ClientSearchAutocomplete;
